import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import setupAtoms, {
  fetchActivityList,
  fetchbomdetails,
  getActivity,
  getConstructionProgressList,
} from "../../atoms/setup/SetupAtoms";
import {
  activityList,
  addConstructionName,
  addProgressToActivities,
  prepareBomColumnsAndGroups,
} from "../../shared/Helper";
import axiosInstance from "../../utils/axios";
import { bomcolumns } from "../../shared/constent/constent";
import { DataGrid } from "@mui/x-data-grid";

const defaultCheckedList = bomcolumns
  .filter((item) => !item.hidden)
  .map((item) => item.key);

const Index = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [checkedList] = useState(defaultCheckedList);
  const [loader, setLoader] = useState(false);
  const [colValue, setColValue] = useState([]);
  const [, setActivity] = useAtom(fetchActivityList);
  const [, setBomDetails] = useAtom(fetchbomdetails);
  const [, setConstruction] = useAtom(getActivity);
  const [, setProgress] = useAtom(getConstructionProgressList);
  const [data] = useAtom(setupAtoms);
  const [list, setList] = useState([]);
  const { constructionList, progressList } = data;
  const { construction } = constructionList;
  const [selected, setSelected] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [, setTableHeight] = useState(0);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const calculateHeight = () => {
    const viewportHeight = window.innerHeight;
    const offset = 308;
    const height = viewportHeight - offset;
    setTableHeight(height);
  };

  useEffect(() => {
    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  const fetchData = async () => {
    setLoader(true);
    try {
      await setBomDetails("");
      await setActivity("");
      await setConstruction("");
      await setProgress("");
    } catch (error) {
      console.error(error);
      showSnackbar("Failed to fetch data", "error");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const updatedColumns = bomcolumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
    setColValue(updatedColumns);
  }, [checkedList]);

  useEffect(() => {
    if (construction) {
      const temp = activityList(construction);
      setList(addProgressToActivities(temp, progressList));
    }
  }, [construction, progressList]);

  useEffect(() => {
    fetchData();
    if (bomcolumns?.length) {
      setColValue(bomcolumns);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (data?.bomList && data?.activityList?.construction) {
      setTableData(
        addConstructionName(data?.bomList, data?.activityList?.construction)
      );
    }
  }, [data]);

  const testingData = (value) => {
    return value?.map((item) => item?.part_name);
  };

  const handleClick = () => {
    setIsEdit((prev) => !prev);
  };

  const handleChange = (value, record, key) => {
    let tempData = { ...record };
    tempData[key] = value;

    const updated = tableData.map((item) =>
      item.bom_id === tempData.bom_id ? tempData : item
    );
    setTableData(updated);

    if (selected.some((s) => s.bom_id === tempData.bom_id)) {
      setSelected((prev) =>
        prev.map((s) => (s.bom_id === tempData.bom_id ? tempData : s))
      );
    } else {
      setSelected((prev) => [...prev, tempData]);
    }
  };

  const handleSave = async () => {
    setBtnLoader(true);
    const value = selected.map((item) => ({
      distribution: item.distribution,
      installed: item.installed,
      bom_id: item.bom_id,
    }));

    try {
      const res = await axiosInstance.post(`/projects/activitymilestone`, value);
      if (res?.data) {
        await setBomDetails("");
        showSnackbar(res?.data?.message || "Saved successfully");
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Save failed. Please try again.", "error");
    } finally {
      setBtnLoader(false);
      setSelected([]);
      setIsEdit(false);
    }
  };

  const { visibleColumns, columnGroupingModel } = prepareBomColumnsAndGroups(
    colValue,
    testingData(
      addConstructionName(data?.bomList, data?.activityList?.construction)
    ),
    isEdit,
    list,
    handleChange
  );

  const rows = tableData?.map((row, index) => ({ id: index, ...row }));

  return (
    <Box px={4} py={2} mt={1.5}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={3} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold", pb: 1 }}>
            Bill of Material
          </Typography>
        </Box>

        <Box display="flex" gap={2} alignItems="center">
          {!isEdit ? (
            <Button variant="contained" onClick={handleClick}>
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={btnLoader}
              >
                {btnLoader ? <CircularProgress size={20} /> : "Save"}
              </Button>
              <Button variant="outlined" onClick={handleClick}>
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      <div style={{ height: "71vh", width: "100%", backgroundColor: "#ffffff" }}>
        <DataGrid
          rows={rows}
          columns={visibleColumns}
          experimentalFeatures={{ columnGrouping: true }}
          columnGroupingModel={columnGroupingModel}
          hideFooterPagination
          hideFooter={true}
          disableColumnMenu
          disableSelectionOnClick
          pagination={false}
          loading={loader}
          sx={{
            "& .MuiDataGrid-columnSeparator": {
              color: "#fff",
            },
            "& .MuiDataGrid-columnHeader": {
              fontWeight: 600,
              backgroundColor: "#f16253",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader--block_name": {
              backgroundColor: "#f16253",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnGroupHeader": {
              backgroundColor: "#f16253",
              color: "#ffffff",
              fontWeight: 600,
            },
            "& .installed-header": {
              backgroundColor: "#f16253",
            },
            "& .distributed-header": {
              backgroundColor: "#f16253",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              width: "100%",
            },
            "& .header-group": {
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
              width: "100%",
            },
          }}
        />
      </div>

      {/* Snackbar Component */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Index;
